/**
 * SessionLauncher — the custom study/exam session composer.
 *
 * Rebuilt from scratch (feat/session-spec-builder): a focused single-panel
 * composer with a persistent live preview. The learner assembles a spec
 * (certification, mode, topics, difficulty, question types, count, smart
 * filters) in a scrollable body; a sticky footer reads the assembled spec back
 * as a live "receipt" — the matching-question count is the hero number and
 * gates the launch. A filter combo that matches zero questions disables the
 * launch and explains why, instead of creating an empty session.
 *
 * Backend contract is unchanged: this still POSTs to `createSession` with the
 * unified `filter_spec` (SessionSpec) payload — presentation only.
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useTopics, useSubtopics } from '../../hooks/useQuestions';
import { useQuestionCount } from '../../hooks/useQuestionCount';
import { createSession } from '../../api/sessions';
import { fetchUserDashboard } from '../../api/admin';
import { useCertStore } from '../../stores/certStore';
import type { UserDashboardData } from '../../types/admin';
import { QUESTION_TYPES } from '../../types';
import type { SessionSpec } from '../../types';
import { Button, CMIcon } from '../ui';

type Mode = 'study' | 'exam';

const DIFFICULTIES = ['easy', 'medium', 'hard'] as const;
type Difficulty = (typeof DIFFICULTIES)[number];

const QUESTION_TYPE_LABEL: Record<(typeof QUESTION_TYPES)[number], string> = {
  single: 'dashboard.study.launcher.questionTypeSingle',
  multiple: 'dashboard.study.launcher.questionTypeMultiple',
  yesno: 'dashboard.study.launcher.questionTypeYesno',
  hotspot: 'dashboard.study.launcher.questionTypeHotspot',
  dragdrop: 'dashboard.study.launcher.questionTypeDragdrop',
};

function generateSeed(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Auto-generated placeholder name: "Study · Networking · Jul 8". */
function generateSessionName(mode: Mode, topics: string[], t: TFunction): string {
  const modeLabel =
    mode === 'study'
      ? t('dashboard.study.launcher.modeStudy')
      : t('dashboard.study.launcher.modeExam');
  const topicLabel =
    topics.length === 0
      ? t('dashboard.study.launcher.allTopics')
      : topics.length <= 2
        ? topics.join(', ')
        : t('dashboard.study.launcher.topicsCount', { count: topics.length });
  const dateLabel = new Date().toLocaleDateString(t('dashboard.study.launcher.dateLocale'), {
    month: 'short',
    day: 'numeric',
  });
  return t('dashboard.study.launcher.sessionNamePrefix', {
    mode: modeLabel,
    topics: topicLabel,
    date: dateLabel,
  });
}

/* ── Small building blocks ──────────────────────────────────────────────── */

/** Eyebrow header that opens a grouped section — labels, never numbers. */
function SectionHead({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.11em] text-slate-500 dark:text-slate-400">
      <CMIcon name={icon} size={14} className="text-blue-600 dark:text-blue-400" />
      {children}
    </div>
  );
}

/** A rounded toggle pill (topics, difficulty, question types). */
function TogglePill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-900
        ${
          active
            ? 'bg-blue-600 text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)] hover:bg-blue-700'
            : 'border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10'
        }`}
    >
      {children}
    </button>
  );
}

/* ── Component ──────────────────────────────────────────────────────────── */

export interface SessionLauncherProps {
  /** Close the hosting dialog (rendered as the header's close control). */
  onClose?: () => void;
}

export default function SessionLauncher({ onClose }: SessionLauncherProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const certCode = useCertStore((s) => s.certCode);
  const certifications = useCertStore((s) => s._certifications);
  const setCertification = useCertStore((s) => s.setCertification);

  // ── Form state ──────────────────────────────────────────────────────────
  const [mode, setMode] = useState<Mode>('study');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [selectedSubtopics, setSelectedSubtopics] = useState<string[]>([]);
  const [subtopicQuery, setSubtopicQuery] = useState('');
  const [selectedDifficulties, setSelectedDifficulties] = useState<Difficulty[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [questionCount, setQuestionCount] = useState<number | null>(25);
  const [customCount, setCustomCount] = useState('');
  const [onlyWrong, setOnlyWrong] = useState(false);
  const [onlyUnseen, setOnlyUnseen] = useState(false);
  const [weakTopicsMode, setWeakTopicsMode] = useState(false);
  const [sessionName, setSessionName] = useState('');

  // ── Data ────────────────────────────────────────────────────────────────
  const { data: topics, isLoading: topicsLoading } = useTopics();
  // Subtopics only make sense within ONE domain — offered when exactly one
  // topic is selected. Lets the learner drill a single skill (e.g. Bastion).
  const singleTopic = selectedTopics.length === 1 ? selectedTopics[0] : undefined;
  const { data: subtopics } = useSubtopics(singleTopic, !!singleTopic);
  const availableSubtopics = useMemo(
    () => (subtopics ?? []).filter((s) => s.count > 0),
    [subtopics],
  );
  // Search filter — keep already-selected subtopics visible even when they
  // don't match the query, so a selection is never hidden mid-search.
  const displayedSubtopics = useMemo(() => {
    const q = subtopicQuery.trim().toLowerCase();
    if (!q) return availableSubtopics;
    return availableSubtopics.filter(
      (s) => selectedSubtopics.includes(s.name) || s.name.toLowerCase().includes(q),
    );
  }, [availableSubtopics, subtopicQuery, selectedSubtopics]);
  const { data: dashboard } = useQuery<UserDashboardData>({
    queryKey: ['user-dashboard', certCode],
    queryFn: () => fetchUserDashboard(certCode),
    staleTime: 60_000,
  });

  const weakTopicNames = useMemo(
    () =>
      (dashboard?.weak_topics ?? [])
        .filter((w: { accuracy_percent: number }) => w.accuracy_percent < 60)
        .map((w: { topic: string }) => w.topic),
    [dashboard],
  );

  // Live matching-question count for the current filter combo. Weak-topics mode
  // is expressed by pre-selecting the weak topics, so it needs no extra param.
  const countParams = useMemo(
    () => ({
      topic: selectedTopics.length > 0 ? selectedTopics.join(',') : undefined,
      subtopic: selectedSubtopics.length > 0 ? selectedSubtopics.join(',') : undefined,
      difficulty: selectedDifficulties.length > 0 ? selectedDifficulties.join(',') : undefined,
      question_type: selectedTypes.length > 0 ? selectedTypes.join(',') : undefined,
      exclude_answered: onlyUnseen,
      only_wrong: onlyWrong,
    }),
    [selectedTopics, selectedSubtopics, selectedDifficulties, selectedTypes, onlyUnseen, onlyWrong],
  );

  const { data: countData, isFetching: countFetching } = useQuestionCount(countParams);
  const countReady = countData !== undefined;
  const matchingCount = countData?.count ?? 0;

  // Questions the launched session will actually hold. `count === null` (study
  // "All") means every match; otherwise cap the request at what's available.
  const sessionSize =
    questionCount !== null ? Math.min(questionCount, matchingCount) : matchingCount;

  const placeholderName = generateSessionName(mode, selectedTopics, t);
  const effectiveName = sessionName.trim() || placeholderName;

  const noMatch = countReady && !countFetching && matchingCount === 0;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleModeChange = useCallback((next: Mode) => {
    setMode(next);
    // Exam has no "All" option — settle on a sane default count when arriving.
    setQuestionCount((prev) => (next === 'exam' && prev === null ? 40 : prev));
  }, []);

  const handleCertChange = useCallback(
    (code: string) => {
      if (code === certCode) return;
      setCertification(code);
      // Topics are cert-specific; drop stale selections (and weak mode built on them).
      setSelectedTopics([]);
      setSelectedSubtopics([]);
      setWeakTopicsMode(false);
    },
    [certCode, setCertification],
  );

  const toggleTopic = useCallback((topic: string) => {
    setWeakTopicsMode(false);
    // Subtopics belong to a single domain — any topic change resets them.
    setSelectedSubtopics([]);
    setSubtopicQuery('');
    setSelectedTopics((prev) =>
      prev.includes(topic) ? prev.filter((x) => x !== topic) : [...prev, topic],
    );
  }, []);

  const clearTopics = useCallback(() => {
    setWeakTopicsMode(false);
    setSelectedSubtopics([]);
    setSubtopicQuery('');
    setSelectedTopics([]);
  }, []);

  const toggleSubtopic = useCallback((name: string) => {
    setSelectedSubtopics((prev) =>
      prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name],
    );
  }, []);

  const toggleDifficulty = useCallback((d: Difficulty) => {
    setSelectedDifficulties((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
    );
  }, []);

  const toggleType = useCallback((type: string) => {
    setSelectedTypes((prev) => {
      const next = prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type];
      // Selecting every type is the same as "all" — normalise back to empty.
      return next.length === QUESTION_TYPES.length ? [] : next;
    });
  }, []);

  const toggleWeak = useCallback(() => {
    setSelectedSubtopics([]);
    setWeakTopicsMode((prev) => {
      const next = !prev;
      setSelectedTopics(next ? weakTopicNames : []);
      return next;
    });
  }, [weakTopicNames]);

  const applyCustomCount = useCallback(() => {
    const n = Number(customCount);
    if (Number.isFinite(n) && n >= 5) {
      setQuestionCount(Math.floor(n));
      setCustomCount('');
    }
  }, [customCount]);

  // Quick-start presets seed the composer (they don't launch) so the learner
  // can start from a template and tweak.
  const applyPreset = useCallback(
    (preset: 'weak' | 'hard' | 'unseen' | 'reset') => {
      setSelectedTypes([]);
      setSelectedSubtopics([]);
      setCustomCount('');
      switch (preset) {
        case 'weak':
          setMode('study');
          setSelectedTopics(weakTopicNames);
          setWeakTopicsMode(true);
          setSelectedDifficulties([]);
          setOnlyWrong(false);
          setOnlyUnseen(false);
          setQuestionCount(25);
          break;
        case 'hard':
          setMode('study');
          setSelectedTopics([]);
          setWeakTopicsMode(false);
          setSelectedDifficulties(['hard']);
          setOnlyWrong(false);
          setOnlyUnseen(false);
          setQuestionCount(25);
          break;
        case 'unseen':
          setMode('study');
          setSelectedTopics([]);
          setWeakTopicsMode(false);
          setSelectedDifficulties([]);
          setOnlyWrong(false);
          setOnlyUnseen(true);
          setQuestionCount(25);
          break;
        case 'reset':
          setMode('study');
          setSelectedTopics([]);
          setWeakTopicsMode(false);
          setSelectedDifficulties([]);
          setSelectedTypes([]);
          setOnlyWrong(false);
          setOnlyUnseen(false);
          setQuestionCount(25);
          setSessionName('');
          break;
      }
    },
    [weakTopicNames],
  );

  // ── Launch ────────────────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: (payload: Parameters<typeof createSession>[0]) => createSession(payload, certCode),
    onSuccess: (session) => {
      void queryClient.invalidateQueries({ queryKey: ['my-sessions'] });
      onClose?.();
      navigate(`/study/session/${session.id}`);
    },
  });

  const canLaunch = matchingCount > 0 && !createMut.isPending;

  const handleLaunch = () => {
    if (!canLaunch) return;
    const seed = generateSeed();
    const spec: SessionSpec = {
      v: 1,
      certCode,
      mode,
      topics: selectedTopics,
      subtopics: selectedSubtopics,
      difficulties: selectedDifficulties,
      questionTypes: selectedTypes,
      count: questionCount,
      smart: { onlyWrong, onlyUnseen, weakTopics: weakTopicsMode },
      sessionName: effectiveName,
      seed,
    };
    createMut.mutate({
      session_name: effectiveName,
      mode,
      seed,
      page_size: 25,
      total_questions: sessionSize,
      filter_spec: spec,
    });
  };

  // ── Derived preview copy ────────────────────────────────────────────────
  const topicsSummary =
    selectedTopics.length === 0
      ? t('dashboard.study.launcher.allTopics')
      : selectedTopics.length <= 2
        ? selectedTopics.join(', ')
        : t('dashboard.study.launcher.topicsCount', { count: selectedTopics.length });

  const difficultySummary =
    selectedDifficulties.length === 0
      ? t('dashboard.study.builder.allLevels')
      : selectedDifficulties.map((d) => t(`dashboard.study.launcher.difficulty${d[0].toUpperCase()}${d.slice(1)}`)).join(', ');

  const smartSummary = [
    onlyWrong && t('dashboard.study.builder.smartWrongShort'),
    onlyUnseen && t('dashboard.study.builder.smartUnseenShort'),
    weakTopicsMode && t('dashboard.study.builder.smartWeakShort'),
  ].filter(Boolean) as string[];

  const countPresets = mode === 'exam' ? [10, 20, 40, 60] : [10, 20, 30, 50];

  const smartRows = [
    {
      key: 'wrong' as const,
      icon: 'target',
      on: onlyWrong,
      toggle: () => setOnlyWrong((p) => !p),
      label: t('dashboard.study.smartFilters.wrongLabel'),
      desc: t('dashboard.study.smartFilters.wrongDescription'),
      disabled: false,
    },
    {
      key: 'unseen' as const,
      icon: 'sparkles',
      on: onlyUnseen,
      toggle: () => setOnlyUnseen((p) => !p),
      label: t('dashboard.study.smartFilters.unseenLabel'),
      desc: t('dashboard.study.smartFilters.unseenDescription'),
      disabled: false,
    },
    {
      key: 'weak' as const,
      icon: 'activity',
      on: weakTopicsMode,
      toggle: toggleWeak,
      label: t('dashboard.study.smartFilters.weakLabel'),
      desc: t('dashboard.study.smartFilters.weakDescription'),
      disabled: weakTopicNames.length === 0,
    },
  ];

  return (
    <div className="flex max-h-[86vh] flex-col">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 sm:px-7 dark:border-white/10">
        <div>
          <h2 className="text-[19px] font-extrabold tracking-[-0.02em] text-slate-900 dark:text-white">
            {t('dashboard.study.builder.title')}
          </h2>
          <p className="mt-0.5 text-[13px] leading-snug text-slate-500 dark:text-slate-400">
            {t('dashboard.study.builder.subtitle')}
          </p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('study.mix.custom.close')}
            className="-mr-1 flex-none rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/5 dark:hover:text-slate-200"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </header>

      {/* ── Body (scrolls) ──────────────────────────────────────────────── */}
      <div className="flex-1 space-y-7 overflow-y-auto px-5 py-6 sm:px-7">
        {/* Quick start presets */}
        <div>
          <SectionHead icon="zap">{t('dashboard.study.builder.presetsLabel')}</SectionHead>
          <div className="flex flex-wrap gap-2">
            {weakTopicNames.length > 0 && (
              <TogglePill active={false} onClick={() => applyPreset('weak')}>
                <CMIcon name="activity" size={14} /> {t('dashboard.study.builder.presetWeak')}
              </TogglePill>
            )}
            <TogglePill active={false} onClick={() => applyPreset('hard')}>
              <CMIcon name="flame" size={14} /> {t('dashboard.study.builder.presetHard')}
            </TogglePill>
            <TogglePill active={false} onClick={() => applyPreset('unseen')}>
              <CMIcon name="sparkles" size={14} /> {t('dashboard.study.builder.presetUnseen')}
            </TogglePill>
            <TogglePill active={false} onClick={() => applyPreset('reset')}>
              <CMIcon name="settings" size={14} /> {t('dashboard.study.builder.presetReset')}
            </TogglePill>
          </div>
        </div>

        {/* Mode */}
        <fieldset>
          <legend className="sr-only">{t('dashboard.study.launcher.chooseMode')}</legend>
          <SectionHead icon="layers">{t('dashboard.study.launcher.chooseMode')}</SectionHead>
          <div className="grid grid-cols-2 gap-3">
            {(
              [
                { key: 'study' as const, icon: 'brain', title: t('dashboard.study.modes.free.title'), desc: t('dashboard.study.modes.free.description') },
                { key: 'exam' as const, icon: 'clock', title: t('dashboard.study.modes.practice.title'), desc: t('dashboard.study.modes.practice.description') },
              ]
            ).map((m) => {
              const active = mode === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => handleModeChange(m.key)}
                  aria-pressed={active}
                  className={`flex flex-col gap-2 rounded-2xl border p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                    ${
                      active
                        ? 'border-blue-500 bg-blue-50/70 ring-1 ring-blue-500/30 dark:border-blue-400/50 dark:bg-blue-500/10'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10'
                    }`}
                >
                  <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${active ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400'}`}>
                    <CMIcon name={m.icon} size={18} />
                  </span>
                  <span className="text-[14px] font-extrabold tracking-[-0.01em] text-slate-900 dark:text-white">{m.title}</span>
                  <span className="text-[12px] leading-snug text-slate-500 dark:text-slate-400">{m.desc}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        {/* Scope: cert, topics, difficulty, types */}
        <div className="space-y-5">
          <SectionHead icon="target">{t('dashboard.study.builder.secScope')}</SectionHead>

          {/* Certification */}
          <div>
            <label
              htmlFor="builder-cert"
              className="mb-1.5 block text-[13px] font-semibold text-slate-700 dark:text-slate-300"
            >
              {t('dashboard.study.launcher.certLegend')}
            </label>
            <select
              id="builder-cert"
              value={certCode}
              onChange={(e) => handleCertChange(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-[3px] focus:ring-blue-500/20 dark:border-white/10 dark:bg-slate-900/80 dark:text-white"
            >
              {certifications.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.provider.name} — {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Topics */}
          <fieldset>
            <div className="mb-2 flex items-center justify-between">
              <legend className="text-[13px] font-semibold text-slate-700 dark:text-slate-300">
                {t('dashboard.study.topics.legend')}
              </legend>
              {selectedTopics.length > 0 && (
                <button
                  type="button"
                  onClick={clearTopics}
                  className="text-[12px] font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  {t('dashboard.study.topics.all')}
                </button>
              )}
            </div>
            {topicsLoading ? (
              <p className="text-sm text-slate-400">{t('dashboard.study.topics.loading')}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                <TogglePill active={selectedTopics.length === 0} onClick={clearTopics}>
                  {t('dashboard.study.topics.all')}
                </TogglePill>
                {(topics ?? []).map((tp) => (
                  <TogglePill
                    key={tp.topic}
                    active={selectedTopics.includes(tp.topic)}
                    onClick={() => toggleTopic(tp.topic)}
                  >
                    {tp.topic}
                    <span className={`font-mono text-[11px] tabular-nums ${selectedTopics.includes(tp.topic) ? 'text-blue-100' : 'text-slate-400'}`}>
                      {tp.count}
                    </span>
                  </TogglePill>
                ))}
              </div>
            )}
          </fieldset>

          {/* Subtopics — narrow drill, shown when exactly one domain is picked */}
          {singleTopic && availableSubtopics.length > 0 && (
            <fieldset>
              <div className="mb-2 flex items-center justify-between">
                <legend className="text-[13px] font-semibold text-slate-700 dark:text-slate-300">
                  {t('dashboard.study.subtopics.legend')}
                </legend>
                {selectedSubtopics.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedSubtopics([])}
                    className="text-[12px] font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    {t('dashboard.study.subtopics.all')}
                  </button>
                )}
              </div>
              {availableSubtopics.length > 6 && (
                <div className="relative mb-2">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="7" />
                      <path d="m20 20-3.5-3.5" />
                    </svg>
                  </span>
                  <input
                    type="search"
                    value={subtopicQuery}
                    onChange={(e) => setSubtopicQuery(e.target.value)}
                    placeholder={t('dashboard.study.subtopics.searchPlaceholder')}
                    aria-label={t('dashboard.study.subtopics.searchPlaceholder')}
                    className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 transition-colors focus:border-blue-500 focus:outline-none focus:ring-[3px] focus:ring-blue-500/20 dark:border-white/10 dark:bg-slate-900/80 dark:text-white dark:placeholder-slate-500"
                  />
                </div>
              )}
              {displayedSubtopics.length === 0 ? (
                <p className="text-[12.5px] text-slate-400 dark:text-slate-500">
                  {t('dashboard.study.subtopics.noResults', { query: subtopicQuery })}
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {displayedSubtopics.map((s) => (
                    <TogglePill
                      key={s.name}
                      active={selectedSubtopics.includes(s.name)}
                      onClick={() => toggleSubtopic(s.name)}
                    >
                      {s.name}
                      <span className={`font-mono text-[11px] tabular-nums ${selectedSubtopics.includes(s.name) ? 'text-blue-100' : 'text-slate-400'}`}>
                        {s.count}
                      </span>
                    </TogglePill>
                  ))}
                </div>
              )}
              <p className="mt-1.5 text-[11.5px] leading-snug text-slate-400 dark:text-slate-500">
                {t('dashboard.study.subtopics.hint')}
              </p>
            </fieldset>
          )}

          {/* Difficulty */}
          <fieldset>
            <legend className="mb-2 text-[13px] font-semibold text-slate-700 dark:text-slate-300">
              {t('dashboard.study.launcher.difficultyLegend')}
            </legend>
            <div className="flex flex-wrap gap-2">
              <TogglePill active={selectedDifficulties.length === 0} onClick={() => setSelectedDifficulties([])}>
                {t('dashboard.study.builder.allLevels')}
              </TogglePill>
              {DIFFICULTIES.map((d) => (
                <TogglePill
                  key={d}
                  active={selectedDifficulties.includes(d)}
                  onClick={() => toggleDifficulty(d)}
                >
                  <span className="capitalize">
                    {t(`dashboard.study.launcher.difficulty${d[0].toUpperCase()}${d.slice(1)}`)}
                  </span>
                </TogglePill>
              ))}
            </div>
          </fieldset>

          {/* Question types */}
          <fieldset>
            <legend className="mb-2 text-[13px] font-semibold text-slate-700 dark:text-slate-300">
              {t('dashboard.study.launcher.questionTypeLegend')}
            </legend>
            <div className="flex flex-wrap gap-2">
              <TogglePill active={selectedTypes.length === 0} onClick={() => setSelectedTypes([])}>
                {t('dashboard.study.launcher.questionTypeAll')}
              </TogglePill>
              {QUESTION_TYPES.map((type) => (
                <TogglePill
                  key={type}
                  active={selectedTypes.includes(type)}
                  onClick={() => toggleType(type)}
                >
                  {t(QUESTION_TYPE_LABEL[type])}
                </TogglePill>
              ))}
            </div>
          </fieldset>
        </div>

        {/* Focus: smart filters */}
        <fieldset>
          <SectionHead icon="brain">{t('dashboard.study.smartFilters.legend')}</SectionHead>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {smartRows.map((row) => (
              <button
                key={row.key}
                type="button"
                onClick={row.toggle}
                disabled={row.disabled}
                aria-pressed={row.on}
                className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50
                  ${
                    row.on
                      ? 'border-blue-500/50 bg-blue-50/70 dark:border-blue-400/40 dark:bg-blue-500/10'
                      : 'border-slate-200 bg-white hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10'
                  }`}
              >
                <span className={`mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-lg ${row.on ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400'}`}>
                  <CMIcon name={row.icon} size={15} />
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-slate-900 dark:text-white">{row.label}</span>
                  <span className="mt-0.5 block text-[11.5px] leading-snug text-slate-500 dark:text-slate-400">{row.desc}</span>
                </span>
              </button>
            ))}
          </div>
        </fieldset>

        {/* Length & name */}
        <div className="space-y-4">
          <SectionHead icon="filecheck">{t('dashboard.study.builder.secSize')}</SectionHead>

          <fieldset>
            <legend className="mb-2 text-[13px] font-semibold text-slate-700 dark:text-slate-300">
              {t('dashboard.study.questionCount.label')}
            </legend>
            <div className="flex flex-wrap items-center gap-2">
              {countPresets.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setQuestionCount(n)}
                  className={`min-w-[48px] rounded-lg px-3 py-1.5 font-mono text-sm font-bold tabular-nums transition-colors
                    ${
                      questionCount === n
                        ? 'bg-blue-600 text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)]'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10'
                    }`}
                >
                  {n}
                </button>
              ))}
              {mode === 'study' && (
                <button
                  type="button"
                  onClick={() => setQuestionCount(null)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors
                    ${
                      questionCount === null
                        ? 'bg-blue-600 text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)]'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10'
                    }`}
                >
                  {t('dashboard.study.questionCount.all')}
                </button>
              )}
              <input
                type="number"
                min={5}
                inputMode="numeric"
                placeholder={t('dashboard.study.questionCount.customPlaceholder')}
                value={customCount}
                onChange={(e) => setCustomCount(e.target.value)}
                onBlur={applyCustomCount}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyCustomCount();
                  }
                }}
                aria-label={t('dashboard.study.questionCount.customPlaceholder')}
                className="w-24 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-mono text-sm tabular-nums text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-[3px] focus:ring-blue-500/20 dark:border-white/10 dark:bg-white/5 dark:text-white"
              />
            </div>
          </fieldset>

          <div>
            <label
              htmlFor="builder-name"
              className="mb-1.5 block text-[13px] font-semibold text-slate-700 dark:text-slate-300"
            >
              {t('dashboard.study.launcher.sessionNameLabel')}
            </label>
            <input
              id="builder-name"
              type="text"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              placeholder={placeholderName}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 transition-colors focus:border-blue-500 focus:outline-none focus:ring-[3px] focus:ring-blue-500/20 dark:border-white/10 dark:bg-slate-900/80 dark:text-white dark:placeholder-slate-500"
            />
          </div>
        </div>
      </div>

      {/* ── Live preview / launch bar (sticky footer) ───────────────────── */}
      <footer className="flex-none border-t border-slate-200 bg-white/95 px-5 py-4 backdrop-blur sm:px-7 dark:border-white/10 dark:bg-card-dark/95">
        {noMatch ? (
          /* Zero-match guard — never launch an empty session (production bug). */
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2.5 text-amber-700 dark:text-amber-300">
              <CMIcon name="target" size={18} className="mt-0.5 flex-none" />
              <div>
                <p className="text-[13.5px] font-bold">{t('dashboard.study.launcher.noMatch')}</p>
                <p className="text-[12px] text-amber-600/90 dark:text-amber-300/80">
                  {t('dashboard.study.builder.noMatchHint')}
                </p>
              </div>
            </div>
            <Button type="button" variant="primary" disabled className="w-full sm:w-auto">
              {mode === 'study'
                ? t('dashboard.study.launcher.startStudying')
                : t('dashboard.study.launcher.startExam')}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {/* The receipt */}
            <div className="min-w-0" aria-live="polite">
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[26px] font-extrabold leading-none tabular-nums text-slate-900 dark:text-white" style={{ fontFeatureSettings: '"tnum"' }}>
                  {countReady ? sessionSize : '—'}
                </span>
                <span className="text-[13px] font-semibold text-slate-500 dark:text-slate-400">
                  {countFetching || !countReady
                    ? t('dashboard.study.builder.counting')
                    : t('dashboard.study.builder.matchLabel')}
                </span>
                {countReady && questionCount !== null && matchingCount > sessionSize && (
                  <span className="text-[12px] text-slate-400 dark:text-slate-500">
                    {t('dashboard.study.builder.drawnFrom', { total: matchingCount })}
                  </span>
                )}
              </div>
              <p className="mt-1 truncate text-[12.5px] text-slate-500 dark:text-slate-400">
                <span className="font-semibold text-slate-700 dark:text-slate-300">
                  {mode === 'study'
                    ? t('dashboard.study.launcher.modeStudy')
                    : t('dashboard.study.launcher.modeExam')}
                </span>
                {' · '}
                {topicsSummary}
                {' · '}
                {difficultySummary}
                {smartSummary.length > 0 && ` · ${smartSummary.join(', ')}`}
              </p>
            </div>

            <div className="flex flex-col items-stretch gap-1 sm:items-end">
              <Button
                type="button"
                variant="primary"
                onClick={handleLaunch}
                disabled={!canLaunch}
                className="w-full sm:w-auto"
              >
                <CMIcon name="play" size={15} fill />
                {createMut.isPending
                  ? t('dashboard.study.launcher.creating')
                  : mode === 'study'
                    ? t('dashboard.study.launcher.startStudying')
                    : t('dashboard.study.launcher.startExam')}
              </Button>
              {createMut.isError && (
                <span className="text-[12px] font-medium text-red-600 dark:text-red-400">
                  {t('dashboard.study.launcher.errorCreate')}
                </span>
              )}
            </div>
          </div>
        )}
      </footer>
    </div>
  );
}
